/**
 * React subscription to the host's slash-command list.
 *
 * Thin state wrapper around ``loadCommandsForHost`` so menu UIs (the slash
 * palette in ChatInputBar) render the SAME registry cache that useChat's
 * typed-slash resolution (``lookupCommand`` / ``findCommandBySkillKey``)
 * reads. One network fetch per host feeds both consumers — mounting this
 * hook also warms the cache, so no separate warming effect is needed.
 */
import { useEffect, useRef, useState } from "react";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { Channel } from "../../shared/host";
import { subscribeUserSkillPrefs } from "../../shared/userSkillPrefs";
import { SLASH_COMMANDS_CACHE_TTL_MS, getCommandsForHost, loadCommandsForHost } from "./registry";
import type { SlashCommand } from "./types";

export interface UseCommandListReturn {
  /** Commands for the host — empty until loaded (the menu simply stays closed). */
  readonly commands: readonly SlashCommand[];
  readonly loading: boolean;
  readonly error: string | null;
}

export function useCommandList(
  host: Channel | null,
  apiToken: string | null,
  refreshTokens?: RefreshTokenFn,
): UseCommandListReturn {
  // Seed from the registry cache so a remount with a warm cache renders the
  // menu on the first pass instead of flashing empty.
  const [commands, setCommands] = useState<readonly SlashCommand[]>(() => getCommandsForHost(host));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!host) {
      setCommands([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    setLoading(true);
    loadCommandsForHost(host, apiToken, refreshTokens)
      .then((loaded) => {
        if (!cancelled) {
          setCommands(loaded);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load slash commands");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [host, apiToken, refreshTokens]);

  // Re-read the registry when the user flips a skill toggle in Settings —
  // the chat panel stays mounted behind the Settings overlay, so without
  // this the palette would keep serving the pre-toggle snapshot until a
  // pane reload. getCommandsForHost applies the disable filter on read.
  useEffect(
    () => subscribeUserSkillPrefs(() => setCommands(getCommandsForHost(host))),
    [host],
  );

  // apiToken/refreshTokens are re-created whenever the surrounding auth state
  // refreshes in the background (a routine, frequent occurrence — see
  // AuthStateProvider's proactive/heartbeat refreshers). Reading them through
  // a ref (rather than depending on them directly) keeps the interval below
  // anchored to `host` alone, so a token refresh can't reset its 60s
  // countdown before it ever fires.
  const latestArgsRef = useRef({ apiToken, refreshTokens });
  latestArgsRef.current = { apiToken, refreshTokens };

  // Re-check the persona-scoped list on the same cadence as the registry's
  // own cache TTL. An admin can disable a skill for this persona in Studio at
  // any point during an open pane session — without this, an already-mounted
  // pane would keep offering it until the pane itself was closed and reopened
  // (AIVA-7670). loadCommandsForHost only actually re-fetches once the cache
  // has gone stale, so this just gives that expiry a chance to be noticed.
  useEffect(() => {
    if (!host) return;
    const interval = setInterval(() => {
      const { apiToken: token, refreshTokens: refresh } = latestArgsRef.current;
      loadCommandsForHost(host, token, refresh)
        .then((loaded) => setCommands(loaded))
        .catch(() => {
          // Keep showing the last good list on a background refresh failure —
          // the mount-time effect above already surfaces load errors.
        });
    }, SLASH_COMMANDS_CACHE_TTL_MS);
    return () => clearInterval(interval);
  }, [host]);

  return { commands, loading, error };
}
